/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef, useState } from 'react'
import {
  AbstractCropperInstanceCallback,
  CircleStencil,
  RectangleStencil,
  ExtendedSettings,
  FixedCropper,
  FixedCropperRef,
  FixedCropperSettings,
  ImageRestriction
} from 'react-advanced-cropper'
import IconButton from '@/shared/components/IconButton/IconButton'
import FlipSharpIcon from '@mui/icons-material/FlipSharp'
import Slider from '@/shared/components/Slider/Slider'
import Button from '@/shared/components/Button/Button'
import { useTranslation } from 'react-i18next'
import {
  getAbsoluteZoom,
  getZoomFactor
} from 'advanced-cropper/extensions/absolute-zoom'
import Dropzone from '@/shared/components/Dropzone/Dropzone'
import { AbstractCropperRef } from 'react-advanced-cropper/dist/components/AbstractCropper'
import { useImageSlots } from '@/shared/store/useImageSlots'
import styles from './ImageCropper.module.scss'

type onChangeType = AbstractCropperInstanceCallback<
  AbstractCropperRef<ExtendedSettings<FixedCropperSettings>>
>

type imageSize = { width: number; height: number }
type stencilSize = { width: number; height: number }

interface ImageCropperProps {
  slotId: string
  imageSize: imageSize
  stencilSize: stencilSize
  onModalClose: () => void
  imageUrl?: string
  shape?: 'circle' | 'rectangle'
}

function ImageCropper(props: ImageCropperProps) {
  const {
    imageSize,
    imageUrl,
    stencilSize,
    onModalClose,
    shape = 'rectangle',
    slotId
  } = props

  const cropperRef = useRef<FixedCropperRef>(null)

  const slot = useImageSlots(s => s.slots[slotId])
  const setOriginal = useImageSlots(s => s.setOriginal)
  const setCropped = useImageSlots(s => s.setCropped)
  const clearSlot = useImageSlots(s => s.clear)
  const { t } = useTranslation()

  const [previewActive, setPreviewActive] = useState(false)
  const [adjustmentActive, setAdjustmentActive] = useState(false)
  const [overlayClassName, setOverlayClassName] = useState(styles.overlay)
  const [isCleared, setIsCleared] = useState(false)

  const cropper = cropperRef.current
  const [absoluteZoom, setZoom] = useState<number | null>(null)

  const handleImageDrop = (file: File) => {
    setOriginal(slotId, file)
    setIsCleared(false)
  }

  const flip = (horizontal: boolean, vertical: boolean) =>
    cropperRef.current?.flipImage(horizontal, vertical)

  const rotate = (angle: number) => cropperRef.current?.rotateImage(angle)

  const getOverlayClassName = () => {
    if (adjustmentActive)
      return previewActive
        ? styles.overlayInvertedForPreview
        : styles.overlayInverted
    return previewActive ? styles.overlayForPreview : styles.overlay
  }

  useEffect(() => {
    setOverlayClassName(getOverlayClassName())
  }, [adjustmentActive, previewActive])

  const handleCropAndStore = () => {
    const canvas = cropperRef.current?.getCanvas()
    if (!canvas) return
    canvas.toBlob(blob => {
      if (!blob) return
      setCropped(slotId, blob, 'image/webp')
      onModalClose()
    }, 'image/webp')
  }

  const handleCropperReady = () => {
    if (cropper?.getState && cropper?.getSettings) {
      setZoom(getAbsoluteZoom(cropper.getState(), cropper.getSettings()))
    }
  }

  const onZoom = (value: number, transitions: boolean) => {
    if (cropper?.getState && cropper?.getSettings) {
      const state = cropper.getState()
      const settings = cropper.getSettings()
      const factor = getZoomFactor(state, settings, value)
      cropper.zoomImage(factor, { transitions })
      setZoom(getAbsoluteZoom(cropper.getState(), cropper.getSettings()))
    }
  }

  const onZoomIn = () => {
    if (!cropper?.getState || !cropper?.getSettings || absoluteZoom === null)
      return
    const newZoom = Math.min(1, absoluteZoom + 0.1)
    const factor = getZoomFactor(
      cropper.getState(),
      cropper.getSettings(),
      newZoom
    )
    cropper.zoomImage(factor, { transitions: true })
    setZoom(getAbsoluteZoom(cropper.getState(), cropper.getSettings()))
  }

  const onZoomOut = () => {
    if (!cropper?.getState || !cropper?.getSettings || absoluteZoom === null)
      return
    const newZoom = Math.max(0, absoluteZoom - 0.1)
    const factor = getZoomFactor(
      cropper.getState(),
      cropper.getSettings(),
      newZoom
    )
    cropper.zoomImage(factor, { transitions: true })
    setZoom(getAbsoluteZoom(cropper.getState(), cropper.getSettings()))
  }

  const handleCropperChange: onChangeType = instance => {
    if (instance?.getState && instance?.getSettings) {
      setZoom(getAbsoluteZoom(instance.getState(), instance.getSettings()))
    }
  }

  const srcForCropper = isCleared
    ? null
    : (slot?.originalUrl ?? imageUrl ?? null)

  return srcForCropper ? (
    <div
      data-click-outside-exception
      className={`imageCropperWrapper ${styles.imageCropperWrapper}`}
    >
      <div className={styles.imageContainer}>
        <div className={styles.imageAndZoomControls}>
          <FixedCropper
            style={{
              flexGrow: 1,
              borderRadius: '0.5rem',
              width: imageSize.width,
              height: imageSize.height
            }}
            ref={cropperRef}
            src={srcForCropper}
            stencilComponent={
              shape === 'rectangle' ? RectangleStencil : CircleStencil
            }
            stencilProps={{
              handlers: false,
              lines: false,
              movable: false,
              resizable: false,
              grid: true,
              overlayClassName
            }}
            stencilSize={{
              width: stencilSize.width,
              height: stencilSize.height
            }}
            imageRestriction={ImageRestriction.stencil}
            onReady={handleCropperReady}
            onChange={handleCropperChange}
          />
          <div className={styles.zoomControls}>
            <IconButton
              icon="las la-search-minus"
              onClick={onZoomOut}
              color="primary"
            />
            <Slider
              zoom={absoluteZoom ?? 0}
              onZoom={onZoom}
              min={0}
              max={1}
              step={0.01}
            />
            <IconButton
              icon="las la-search-plus"
              onClick={onZoomIn}
              color="primary"
            />
          </div>
        </div>
        <div className={styles.additionalControls}>
          <div className={styles.controlsGroup}>
            <IconButton
              icon="las la-undo-alt"
              onClick={() => rotate(-90)}
              color="primary"
            />
            <IconButton
              icon="las la-undo-alt"
              onClick={() => rotate(90)}
              color="primary"
              flip
            />
            <IconButton
              icon={<FlipSharpIcon />}
              onClick={() => flip(true, false)}
              color="primary"
            />
            <IconButton
              icon={<FlipSharpIcon />}
              onClick={() => flip(false, true)}
              color="primary"
              rotate={90}
            />
          </div>
          <div className={styles.controlsGroup}>
            <IconButton
              icon={previewActive ? 'las la-eye-slash' : 'las la-eye'}
              onClick={() => setPreviewActive(!previewActive)}
              color={previewActive ? 'secondary' : 'primary'}
            />
            <IconButton
              icon="las la-adjust"
              onClick={() => setAdjustmentActive(!adjustmentActive)}
              color={adjustmentActive ? 'secondary' : 'primary'}
              flip={adjustmentActive}
            />
          </div>
        </div>
      </div>
      <div className={styles.buttonsContainer}>
        <Button
          color="primary"
          label={t('COMMON.SAVE')}
          variant="contained"
          onClick={handleCropAndStore}
        />
        <Button
          color="secondary"
          label={t('IMAGE_CROPPER.CHANGE_IMAGE')}
          variant="contained"
          onClick={() => {
            clearSlot(slotId)
            setIsCleared(true)
          }}
        />
      </div>
    </div>
  ) : (
    <Dropzone
      onImageDrop={handleImageDrop}
      size={{ height: imageSize.height, width: imageSize.width }}
    />
  )
}

export default ImageCropper
